import type { TextItem } from 'pdfjs-dist/types/src/display/api';

/**
 * Texto do PDF da proposta, com as linhas reconstruídas pela posição na página (de cima
 * para baixo, da esquerda para a direita). Colunas distantes viram " | " para separar
 * rótulo e valor — é o formato que `parseUnimedText` espera. Lê até 12 páginas e ignora
 * letras soltas e marcas d'água. O pdf.js só é baixado quando alguém lê um PDF.
 */
export async function readPdfText(file: File): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const pdf = await task.promise;

  let out = '';
  for (let pageNumber = 1; pageNumber <= Math.min(pdf.numPages, 12); pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    const items = content.items
      .filter((item): item is TextItem => 'str' in item)
      .filter((item) => {
        const value = item.str.trim();
        if (!value || /^([A-Z] ){3,}[A-Z]$/.test(value)) return false;
        // Letra solta grande ou girada: marca d'água.
        const [a, b, , d] = item.transform as number[];
        return !(
          value.length === 1 &&
          /[A-Z]/.test(value) &&
          (Math.abs(a) > 12 || Math.abs(b) > 0.5 || Math.abs(d) > 12)
        );
      })
      .map((item) => ({
        text: item.str,
        x: (item.transform as number[])[4],
        y: (item.transform as number[])[5],
        width: item.width || 0,
      }))
      .sort((first, second) => second.y - first.y || first.x - second.x);

    const rows: Array<{ y: number; items: typeof items }> = [];
    for (const item of items) {
      const row = rows.find((candidate) => Math.abs(candidate.y - item.y) <= 2.5);
      if (row) row.items.push(item);
      else rows.push({ y: item.y, items: [item] });
    }
    rows.sort((first, second) => second.y - first.y);
    const lines = rows
      .map((row) => {
        row.items.sort((first, second) => first.x - second.x);
        let line = '';
        let end: number | null = null;
        for (const item of row.items) {
          if (end !== null) {
            const gap = item.x - end;
            line += gap > 14 ? ' | ' : gap > 1.5 ? ' ' : '';
          }
          line += item.text;
          end = item.x + item.width;
        }
        return line
          .replace(/[ \t]+/g, ' ')
          .replace(/( \| )+/g, ' | ')
          .trim();
      })
      .filter((line) => line && line !== '|');
    out += `\n--- página ${pageNumber} ---\n${lines.join('\n')}`;
  }
  await task.destroy();
  return out;
}
